import { useEffect, useMemo, useState, useRef } from "react";
import { Polyline, Tooltip, CircleMarker } from "react-leaflet";
import LeafletMap, { FitBounds } from "@/components/map/LeafletMap";
import MarkerClusterLayer from "@/components/map/MarkerClusterLayer";
import BizDetailDialog from "@/components/rider/app/BizDetailDialog";
import OsmAmenityLayers from "@/components/map/OsmAmenityLayers";
import { AmenityToggles } from "@/components/map/AmenityToggles";
import { Maximize2, Crosshair } from "lucide-react";

const LAYER = {
  advertiser: { color: "#F59E0B", label: "Places" },
  attraction: { color: "#EC4899", label: "Attractions" },
  bike_shop: { color: "#3B82F6", label: "Bike Shops" },
  bathroom: { color: "#8B5CF6", label: "Bathrooms" },
  parking: { color: "#EAB308", label: "Parking" },
  trailhead: { color: "#10B981", label: "Trailheads" },
  water_station: { color: "#06B6D4", label: "Water" },
};

const isLL = (c) => Array.isArray(c) && Number.isFinite(c[0]) && Number.isFinite(c[1]);

export default function RiderTrailMap({ boundary = [], rel = {}, centerline = null, lines = null, height = 320, onExpand, fullscreen = false, drive = null }) {
  const amen = rel.amenities || {};
  const osm = rel.osm_amenities || {};
  const [amenVisible, setAmenVisible] = useState({});
  const toggleAmen = (k) => setAmenVisible((s) => ({ ...s, [k]: !s[k] }));
  // Admin/partner-approved restroom & parking pins share the Bathrooms/Parking buttons with map-data spots;
  // they start switched on whenever such pins exist.
  const adminBath = (amen.bathrooms || []).filter((x) => x.lat != null).length;
  const adminPark = (amen.parking || []).filter((x) => x.lat != null).length;
  useEffect(() => {
    setAmenVisible((s) => ({ ...s, ...(adminBath && s.osm_bathroom === undefined ? { osm_bathroom: true } : {}), ...(adminPark && s.osm_parking === undefined ? { osm_parking: true } : {}) }));
  }, [adminBath, adminPark]);
  const amenCounts = { osm_park: (osm.parks || []).length, osm_bathroom: (osm.bathrooms || []).length + adminBath, osm_parking: (osm.parking || []).length + adminPark };
  const anyAmen = amenCounts.osm_park + amenCounts.osm_bathroom + amenCounts.osm_parking > 0;

  // Harvested route geometry (same source as the Explore map): prefer the flat
  // centerline, else flatten the multi-segment `lines`. Already [lat,lng].
  const harvested = useMemo(() => {
    const raw = centerline && centerline.length ? centerline : (lines || []).flat();
    return (raw || []).filter(isLL);
  }, [centerline, lines]);
  const points = useMemo(() => {
    const out = [];
    (rel.advertisers || []).forEach((a) => a.lat != null && out.push({ ...a, _t: "advertiser" }));
    (rel.attractions || []).forEach((a) => a.lat != null && out.push({ ...a, _t: "attraction" }));
    (rel.bike_shops || []).forEach((s) => s.lat != null && out.push({ ...s, _t: "bike_shop" }));
    if (amenVisible.osm_bathroom) (amen.bathrooms || []).forEach((x) => x.lat != null && out.push({ ...x, _t: "bathroom" }));
    if (amenVisible.osm_parking) (amen.parking || []).forEach((x) => x.lat != null && out.push({ ...x, _t: "parking" }));
    (amen.trailheads || []).forEach((x) => x.lat != null && out.push({ ...x, _t: "trailhead" }));
    (amen.water_stations || []).forEach((x) => x.lat != null && out.push({ ...x, _t: "water_station" }));
    return out;
  }, [rel, amen, amenVisible.osm_bathroom, amenVisible.osm_parking]);

  const routes = (rel.routes || []).filter((r) => (r.coordinates?.length || 0) >= 2);
  const [shownRouteIds, setShownRouteIds] = useState(() => new Set());
  const toggleRoute = (id) => setShownRouteIds((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const alwaysRoutes = routes.filter((r) => !r.linked_stop_id);
  const linkedRoutes = routes.filter((r) => r.linked_stop_id);
  const routeForAttraction = (aid) => linkedRoutes.find((r) => r.linked_stop_id === aid);
  const drawnRoutes = [...alwaysRoutes, ...linkedRoutes.filter((r) => shownRouteIds.has(r.id))];
  const shownRouteIdsRef = useRef(shownRouteIds);
  shownRouteIdsRef.current = shownRouteIds;

  // Fit to the actual trail route (not the corridor boundary). Prefer the
  // always-visible trail routes; fall back to all routes, then to markers.
  const bounds = useMemo(() => {
    const dr = (drive?.route?.coordinates || []).filter(isLL);
    if (dr.length >= 2) return dr;
    const rpts = [];
    if (harvested.length >= 2) harvested.forEach((c) => rpts.push(c));
    (alwaysRoutes.length ? alwaysRoutes : routes).forEach((r) => r.coordinates.forEach((c) => rpts.push(c)));
    if (rpts.length >= 2) return rpts;
    const pts = points.map((p) => [p.lat, p.lng]);
    return pts.length >= 2 ? pts : null;
  }, [harvested, alwaysRoutes, routes, points, drive]);

  const center = bounds?.[0] || [41.75, -71.3];
  const usedLayers = [...new Set(points.map((p) => p._t))];
  const [refitNonce, setRefitNonce] = useState(0);

  // Tapping a business/attraction/shop pin opens the shared detail card.
  const [selected, setSelected] = useState(null);
  const selLinked = selected && selected._t === "attraction" ? routeForAttraction(selected.id) : null;

  return (
    <div className={fullscreen ? "relative h-full w-full" : "space-y-2"} data-testid="rider-trail-map">
      <div className={fullscreen ? "h-full w-full" : "relative"}>
      <LeafletMap height={fullscreen ? "100%" : height} center={center} zoom={12} className={fullscreen ? "!rounded-none !border-0 h-full" : ""}>
        {bounds && <FitBounds bounds={bounds} nonce={refitNonce} />}
        {harvested.length >= 2 && (
          <>
            <Polyline positions={harvested} pathOptions={{ color: "#ffffff", weight: 6, opacity: 0.9 }} interactive={false} />
            <Polyline positions={harvested} pathOptions={{ color: "#0ea5e9", weight: 4, opacity: 1 }} />
          </>
        )}
        {drawnRoutes.map((r) => (
          <Polyline key={`r-${r.id}`} positions={r.coordinates} pathOptions={{ color: r.color || "#22D3EE", weight: 5, opacity: 0.9 }}>
            <Tooltip>{r.name}</Tooltip>
          </Polyline>
        ))}
        {points.length > 0 && (
          <MarkerClusterLayer points={points} layerMeta={LAYER}
            routeForAttraction={routeForAttraction} shownRouteIdsRef={shownRouteIdsRef} onToggleRoute={toggleRoute}
            onSelectPoint={setSelected} />
        )}
        <OsmAmenityLayers data={osm} visible={amenVisible} />
        {drive?.route?.coordinates?.length >= 2 && (
          <>
            <Polyline positions={drive.route.coordinates} pathOptions={{ color: "#ffffff", weight: 9, opacity: 0.95 }} interactive={false} />
            <Polyline positions={drive.route.coordinates} pathOptions={{ color: "#2563eb", weight: 6, opacity: 1, dashArray: drive.route.source === "straight_line" ? "8 8" : null }}>
              <Tooltip sticky>Drive to {drive.trailhead?.name}</Tooltip>
            </Polyline>
            <CircleMarker center={drive.route.coordinates[0]} radius={8} pathOptions={{ color: "#ffffff", weight: 3, fillColor: "#2563eb", fillOpacity: 1 }}>
              <Tooltip>You are here</Tooltip>
            </CircleMarker>
          </>
        )}
      </LeafletMap>
      {onExpand && (
        <button type="button" onClick={onExpand} data-testid="rider-map-expand" aria-label="Expand map"
          className="absolute top-2 right-2 z-[1000] w-9 h-9 rounded-lg bg-background/90 backdrop-blur border border-border shadow-md flex items-center justify-center text-foreground hover:bg-background active:scale-95 transition-all">
          <Maximize2 className="w-4 h-4" />
        </button>
      )}
      </div>
      {fullscreen && bounds && (
        <button type="button" onClick={() => setRefitNonce((n) => n + 1)} data-testid="rider-map-recenter"
          className="absolute bottom-3 right-3 z-[1000] inline-flex items-center gap-1.5 rounded-full bg-background/90 backdrop-blur border border-border shadow-md px-3.5 py-2 text-xs font-semibold text-foreground hover:bg-background active:scale-95 transition-all">
          <Crosshair className="w-4 h-4" />Fit to route
        </button>
      )}
      {anyAmen && (
        <div className={fullscreen ? "absolute left-14 top-2 z-[1000] max-w-[calc(100%-4rem)] bg-black/60 backdrop-blur rounded-lg px-2 py-1.5" : ""} data-testid="rider-map-amenity-toggles">
          <AmenityToggles visible={amenVisible} onToggle={toggleAmen} available={amenCounts} />
        </div>
      )}
      {(usedLayers.length > 0 || routes.length > 0) && (
        <div className={fullscreen
          ? "absolute left-2 bottom-2 z-[1000] max-w-[calc(100%-1rem)] flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white bg-black/60 backdrop-blur rounded-lg px-3 py-2"
          : "flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground"} data-testid="rider-map-legend">
          {routes.length > 0 && <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 rounded" style={{ background: "#22D3EE" }} />Routes</span>}
          {usedLayers.map((t) => (
            <span key={t} className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: LAYER[t].color }} />{LAYER[t].label}</span>
          ))}
        </div>
      )}
      <BizDetailDialog
        biz={selected}
        onClose={() => setSelected(null)}
        routeAction={selLinked ? {
          shown: shownRouteIds.has(selLinked.id),
          color: selLinked.color,
          onToggle: () => { toggleRoute(selLinked.id); setSelected(null); },
        } : null}
      />
    </div>
  );
}
